import { type SanityClient } from '@sanity/client'

import {
  frameBuilderConfigurationQuery,
  frameBuilderProductConfigurationQuery,
  frameBuilderStringsQuery,
} from '@frame-builder/data/sanity/queries'
import {
  type SanityFrameBuilderProductQuery,
  type SanityFrameBuilderQuery,
  type SanityFrameBuilderStringsQuery,
} from '@frame-builder/data/sanity/queries/types'

interface SanityLocaleQueryParameter {
  locale: string
}

export const sanityProjectId = process.env
  .NEXT_PUBLIC_SANITY_PROJECT_ID as string
export const sanityDataset = process.env.NEXT_PUBLIC_SANITY_DATASET as string
export const sanityApiVersion = 'v2026-01-01'

if (!sanityProjectId) {
  throw new Error('Missing NEXT_PUBLIC_SANITY_PROJECT_ID')
}

if (!sanityDataset) {
  throw new Error('Missing NEXT_PUBLIC_SANITY_DATASET')
}

/**
 * Fetches data from Sanity using a GROQ query.
 */
export const fetchQuery = async <T, P = Record<string, never>>(
  sanityClient: SanityClient,
  query: string,
  params?: P,
) => sanityClient.fetch<T>(query, params ?? {})

/**
 * Fetches frame builder configuration.
 */
export const getFrameBuilderConfiguration = async (
  sanityClient: SanityClient,
  locale: string,
) =>
  fetchQuery<SanityFrameBuilderQuery, SanityLocaleQueryParameter>(
    sanityClient,
    frameBuilderConfigurationQuery,
    {
      locale,
    },
  )

/**
 * Fetches frame builder product configuration.
 */
export const getFrameBuilderProductConfiguration = async (
  sanityClient: SanityClient,
  locale: string,
) =>
  fetchQuery<SanityFrameBuilderProductQuery, SanityLocaleQueryParameter>(
    sanityClient,
    frameBuilderProductConfigurationQuery,
    {
      locale,
    },
  )

/**
 * Fetches frame builder strings.
 */
export const getFrameBuilderStrings = async (
  sanityClient: SanityClient,
  locale: string,
) =>
  fetchQuery<SanityFrameBuilderStringsQuery, SanityLocaleQueryParameter>(
    sanityClient,
    frameBuilderStringsQuery,
    {
      locale,
    },
  )
